import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
  Grid,
  Stack,
} from '@mui/material'
import { InfoIcon, LeftArrowIcon, RightArrowIcon } from '../../Svgs'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import TakeOffTooltip from '../TakeOffTooltip'
import { createSelector } from '@reduxjs/toolkit'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Can } from '../../../utils/AbilityUtil'
import {
  AttributeActionNames,
  AttributeResourceNames,
} from '../../../models/AccessControl'

const tooltipText = `All pricing displayed is for general informational purposes only and may not reflect final costs, as actual charges vary based on specific circumstances.`

const getMaterialTypeColor = (materialType: string) => {
  switch (materialType) {
    case 'Windows':
      return '#F7D565'
    case 'Exterior Doors':
      return '#65F7F2'
    case 'Interior Doors':
      return '#B496FF'
    case 'Skylights':
      return '#96C7FF'
    case 'Shear Walls':
      return '#FFB000F2'
    case 'Load Bearing Walls':
      return '#FF2E79'
    default:
      return '#D1D1D1'
  }
}

const GroupButton = ({
  groupName,
  isSelected,
  handleClick,
}: {
  groupName: string
  isSelected: boolean
  handleClick: () => void
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isSelected) {
      buttonRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [isSelected])

  return (
    <Button
      ref={buttonRef}
      variant='contained'
      disableElevation
      onClick={handleClick}
      sx={{
        fontSize: '20px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        textTransform: 'capitalize',
        borderRadius: '4px',
        color: isSelected ? 'white' : '#A3A3A3',
        textWrap: 'nowrap',
        minWidth: 'fit-content',
        backgroundColor: isSelected ? '#1705E5' : 'transparent',
      }}
    >
      {groupName.toLowerCase()}
    </Button>
  )
}

type MaterialGroup = {
  [materialType: string]: {
    [material: string]: number
  }
}

const GroupBreakdown = ({ group }: { group: MaterialGroup }) => {
  return (
    <Box
      sx={{
        flex: 1,
        gap: '12px',
        display: 'flex',
        flexDirection: 'row',
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        '&-ms-overflow-style:': {
          display: 'none',
        },
      }}
    >
      {Object.entries(group).map(([materialType, group]) => {
        const groupEntries = Object.entries(group)
        const itemCount = groupEntries.length
        const columnSize = 4
        const maxRowCount = 3
        const columnCount = Math.ceil(itemCount / maxRowCount)

        return (
          <Box
            key={materialType}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#EEEEEE',
              borderRadius: '4px',
            }}
          >
            <Typography
              sx={{
                color: '#04002E',

                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: 'normal',
                padding: '4px 12px',
                backgroundColor: getMaterialTypeColor(materialType),
                borderRadius: '4px 4px 0px 0px',
                textTransform: 'capitalize',
                textWrap: 'nowrap',
              }}
            >
              {materialType.toLowerCase()}
            </Typography>

            <Box sx={{ flexGrow: 1 }}>
              <Grid
                container
                spacing={4}
                columns={columnCount * columnSize}
                sx={{
                  padding: '16px 12px',
                  flexWrap: 'nowrap',
                }}
              >
                {[...Array(columnCount)].map((_, columnIndex) => {
                  const rowCount = Math.min(
                    itemCount - maxRowCount * columnIndex,
                    maxRowCount,
                  )

                  return (
                    <Grid
                      key={`${materialType}-column-${columnIndex}`}
                      size='auto'
                    >
                      <Stack spacing={2}>
                        {[...Array(rowCount)].map((_, rowIndex) => {
                          const itemIndex = rowCount * columnIndex + rowIndex
                          const [materialName, count] = groupEntries[itemIndex]

                          return (
                            <Box
                              key={`${materialName}-row-${columnIndex}-${rowIndex}`}
                              sx={{
                                justifyContent: 'space-between',
                                display: 'flex',
                                gap: '16px',
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#06004A',

                                  fontSize: '14px',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  lineHeight: 'normal',
                                  textTransform: 'capitalize',
                                }}
                              >
                                {materialName.toLowerCase()}
                              </Typography>

                              <Typography
                                sx={{
                                  color: '#06004A',

                                  fontSize: '14px',
                                  fontStyle: 'normal',
                                  fontWeight: 600,
                                  lineHeight: 'normal',
                                }}
                              >
                                {count}
                              </Typography>
                            </Box>
                          )
                        })}
                      </Stack>
                    </Grid>
                  )
                })}
              </Grid>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

type GroupedMaterialBreakdown = {
  [group: string]: MaterialGroup
}

const selectGroupedMaterialBreakdown = createSelector(
  (state: RootState) => state.takeOff.takeOffCalculations?.materialBreakdown,
  (materialBreakdown) => {
    const groupedMaterialBreakdown: GroupedMaterialBreakdown = {}
    // TODO: Temporary solution
    const entireHomeBreakdown = materialBreakdown?.find(
      (item) => item.name === 'Entire Home',
    )
    if (!entireHomeBreakdown) {
      // if (areasBreakdown) {
      return groupedMaterialBreakdown
    }

    // TODO: Temporary solution
    // for (const areaBreakdown of areasBreakdown) {
    // for (const materialGroup of areaBreakdown.materialGroups) {
    for (const materialGroup of entireHomeBreakdown.materialGroups) {
      const groupName = materialGroup.name
      if (!groupedMaterialBreakdown[groupName]) {
        groupedMaterialBreakdown[groupName] = {}
      }

      for (const materialType of materialGroup.materialTypes) {
        const typeName = materialType.name
        if (!groupedMaterialBreakdown[groupName][typeName]) {
          groupedMaterialBreakdown[groupName][typeName] = {}
        }

        for (const material of materialType.materials) {
          const materialName = material.name
          if (!groupedMaterialBreakdown[groupName][typeName][materialName]) {
            groupedMaterialBreakdown[groupName][typeName][materialName] = 0
          }

          groupedMaterialBreakdown[groupName][typeName][materialName] +=
            material.count
        }
      }
      // }
    }

    return groupedMaterialBreakdown
  },
)

const MaterialBreakdown = () => {
  const isTakeOffCalculationInProgress = useSelector(
    (state: RootState) => state.takeOff.isTakeOffCalculationInProgress,
  )
  const groupedMaterialBreakdown = useSelector(selectGroupedMaterialBreakdown)

  const [selectedGroup, setSelectedGroup] = useState<string>()
  useEffect(() => {
    const keys = Object.keys(groupedMaterialBreakdown)
    if (!keys.length) {
      return
    }

    setSelectedGroup((prevSelectedGroup) => {
      if (
        !prevSelectedGroup ||
        (prevSelectedGroup && !keys.includes(prevSelectedGroup))
      ) {
        return keys[0]
      }

      return prevSelectedGroup
    })
  }, [groupedMaterialBreakdown])

  const handleLeftArrowClick = useCallback(() => {
    const keys = Object.keys(groupedMaterialBreakdown)
    if (!keys.length) {
      return
    }

    setSelectedGroup((prevSelectedGroup) => {
      // If there is no selected group, set the first one available
      if (!prevSelectedGroup) {
        return keys[0]
      }

      const index = keys.findIndex((key) => key === prevSelectedGroup)
      // If there is no key that equals to the selected group, set the first one available
      if (index < 0) {
        return keys[0]
      }

      // If selected group is the first element, select the last one
      if (index === 0) {
        return keys[keys.length - 1]
      }

      // Otherwise, select the previous group
      return keys[index - 1]
    })
  }, [groupedMaterialBreakdown])

  const handleRightArrowClick = useCallback(() => {
    const keys = Object.keys(groupedMaterialBreakdown)
    if (!keys.length) {
      return
    }

    setSelectedGroup((prevSelectedGroup) => {
      // If there is no selected group, set the first one available
      if (!prevSelectedGroup) {
        return keys[0]
      }

      const index = keys.findIndex((key) => key === prevSelectedGroup)
      // If there is no key that equals to the selected group, set the first one available
      if (index < 0) {
        return keys[0]
      }

      // If selected group is the last element, select the first one
      if (index === keys.length - 1) {
        return keys[0]
      }

      // Otherwise, select the next group
      return keys[index + 1]
    })
  }, [groupedMaterialBreakdown])

  return (
    <Box
      sx={{
        flex: 1,
        gap: '4px',
        display: 'flex',
        flexDirection: 'column',
        width: 'calc(100% - 584px - 24px - 0.667px)', // 100% - (Project Cost width + Spacial Design Score width) - 2x gap - divider
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
          alignItems: 'baseline',
        }}
      >
        <Typography
          sx={{
            color: '#06004A',

            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        >
          Material Breakdown
        </Typography>

        <TakeOffTooltip title={tooltipText}>
          <InfoIcon sx={{ color: '#04002E', fontSize: 12 }} />
        </TakeOffTooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          gap: '10px',
          display: 'flex',
          padding: '12px',
          borderRadius: '4px',
          flexDirection: 'column',
          backgroundColor: '#F2F2F2',
          overflow: 'hidden',
        }}
      >
        <Can I={AttributeActionNames.Read} a={AttributeResourceNames.TakeOff}>
          {!isTakeOffCalculationInProgress ? (
            <>
              <Box
                sx={{
                  gap: '8px',
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <IconButton onClick={handleLeftArrowClick}>
                  <LeftArrowIcon
                    sx={{
                      color: '#1705E5',
                      fontSize: 20,
                    }}
                  />
                </IconButton>

                <Divider
                  flexItem
                  orientation='vertical'
                  sx={{
                    backgroundColor: '#BBBCBC',
                  }}
                />

                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '12px',
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    '&-ms-overflow-style:': {
                      display: 'none',
                    },
                  }}
                >
                  {Object.keys(groupedMaterialBreakdown).map((groupName) => (
                    <GroupButton
                      key={groupName}
                      groupName={groupName}
                      isSelected={selectedGroup === groupName}
                      handleClick={() => setSelectedGroup(groupName)}
                    />
                  ))}
                </Box>

                <Divider
                  flexItem
                  orientation='vertical'
                  sx={{
                    backgroundColor: '#BBBCBC',
                  }}
                />

                <IconButton onClick={handleRightArrowClick}>
                  <RightArrowIcon
                    sx={{
                      color: '#1705E5',
                      fontSize: 20,
                    }}
                  />
                </IconButton>
              </Box>

              <Divider
                flexItem
                sx={{
                  backgroundColor: '#BBBCBC',
                }}
              />

              {selectedGroup && (
                <GroupBreakdown
                  group={groupedMaterialBreakdown[selectedGroup]}
                />
              )}
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </Can>
      </Box>
    </Box>
  )
}

export default MaterialBreakdown
